import { Link } from "react-router-dom";
import "./AbsencesDetails.css";
import BackButton from "../../../../../sharedComponents/backButton/BackButton";
import { CustomProvider, DatePicker, SelectPicker } from "rsuite";
import de_DE from 'rsuite/locales/de_DE';
import { useEffect, useState } from "react";
import axios from "axios";
import LoadingSpinner from "../../../../../sharedComponents/LoadingSpinner/LoadingSpinner";
import { DataGrid } from '@mui/x-data-grid';
import formatDuration from "../../../../../sharedComponents/FormatDuration";
import parseDuration from "../../../../../sharedComponents/ParseDuration";
import PrimaryButton from "../../../../../sharedComponents/primaryButton/PrimaryButton";
import { Tab, Tabs } from "react-bootstrap";
import FilterArea from "../../../../../sharedComponents/filterArea/FilterArea";
import { MonthNames } from "../../../../../sharedComponents/MonthNames";
import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, ToggleButton, ToggleButtonGroup } from "@mui/material";
import AbsenceMonthMatrix from "./absenceOverview/AbsenceMonthMatrix";
import AbsenceYearSummary from "./absenceOverview/AbsenceYearSummary";
import { getAvailableAbsenceTypes, getFilteredEmployees } from "./absenceOverview/absenceOverviewUtils";

const VIEW_MODE_STORAGE_KEY = "absences-overview-view";

const columns = [
    { field: 'firstName', headerName: 'Vorname', width: 150 },
    { field: 'lastName', headerName: 'Nachname', width: 150 },
    { field: 'type', headerName: "Art", width: 120 },
    { field: 'startTime', headerName: "Start", width: 120 },
    { field: 'endTime', headerName: "Ende", width: 120 },
    { field: 'countOfAbsenceDays', headerName: "Abwesenheitstage", width: 150 },
    { field: 'durationPerDay', headerName: "Stunden pro Abwesenheitstag", width: 200 },
    { field: 'paid', headerName: "Bezahlt", width: 100 },
];

function getInitialViewMode() {
    try {
        return localStorage.getItem(VIEW_MODE_STORAGE_KEY) === "overview" ? "overview" : "list";
    } catch (error) {
        return "list";
    }
}

function AbsencesDetails() {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [absences, setAbsences] = useState([]);
    const [selectedTab, setSelectedTab] = useState("0");

    const [selectedArea, setSelectedArea] = useState(null);
    const [viewMode, setViewMode] = useState(getInitialViewMode);
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
    const [selectedTypes, setSelectedTypes] = useState([]);

    useEffect(() => {
        fetchAbsences();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTab, selectedMonth, selectedYear]);

    const isMonthlyView = String(selectedTab) === "0";

    const fetchAbsences = () => {
        setIsLoading(true);
        setHasError(false);

        // Im Monats-Tab bestimmt der Monats-Picker auch das Jahr.
        const year = isMonthlyView && selectedMonth ? selectedMonth.getFullYear() : selectedYear;
        const month = isMonthlyView && selectedMonth
            ? (selectedMonth.getMonth() + 1).toString().padStart(2, '0')
            : null;

        axios.get(`absence/getAbsencePeriodsForAllEmployees?year=${year}${month ? `&month=${month}` : ''}`)
            .then(results => {
                setAbsences(results.data || []);
                setIsLoading(false);
            })
            .catch(() => {
                setHasError(true);
                setIsLoading(false);
            });
    };

    const flatAndSortAbsences = () => {
        const flattenedAbsences = absences.flatMap(absence =>
            (absence.absencePeriods || []).map(period => ({
                ...period,
                employeeFirstName: absence.employeeFirstName,
                employeeLastName: absence.employeeLastName,
                employeeId: absence.employeeId,
            }))
        );

        return flattenedAbsences.sort((a, b) => new Date(a.absenceDays[0]?.date) - new Date(b.absenceDays[0]?.date));
    };

    const absenceMatchesFilters = (absence) => {
        if (selectedArea !== null && !selectedArea.assigned_employee_ids.includes(absence.employeeId)) {
            return false;
        }
        if (selectedEmployeeIds.length > 0 && !selectedEmployeeIds.includes(absence.employeeId)) {
            return false;
        }
        if (selectedTypes.length > 0 && !selectedTypes.includes(absence.absenceType)) {
            return false;
        }
        return true;
    };

    const generateRows = () => {
        const rows = [];

        flatAndSortAbsences().forEach(absence => {
            if (!absenceMatchesFilters(absence)) return;

            const absenceDays = absence.absenceDays.sort((a, b) => new Date(a.date) - new Date(b.date));
            if (absenceDays.length === 0) return;

            rows.push({
                id: absence.id,
                firstName: absence.employeeFirstName,
                lastName: absence.employeeLastName,
                type: absence.absenceType,
                startTime: new Date(absenceDays[0].date).toLocaleDateString("de-DE", { day: '2-digit', month: '2-digit', year: 'numeric' }),
                endTime: new Date(absenceDays[absenceDays.length - 1].date).toLocaleDateString("de-DE", { day: '2-digit', month: '2-digit', year: 'numeric' }),
                countOfAbsenceDays: absenceDays.length,
                durationPerDay: formatDuration(parseDuration(absenceDays[0].duration)),
                paid: absence.paid ? "Ja" : "Nein",
            });
        });

        return rows;
    };

    const generateDataTable = () => {
        return (
            <DataGrid
                rows={generateRows()}
                columns={columns}
                disableRowSelectionOnClick
                hideFooterPagination
                density="compact"
                disableColumnMenu
                hideFooter
                className="absences-data-grid"
            />
        );
    };

    const changeTab = (tabIndex) => {
        const normalizedTabIndex = String(tabIndex);
        // Beim Wechsel in die Jahresansicht das Jahr des zuletzt gewählten Monats übernehmen.
        if (normalizedTabIndex === "1" && selectedMonth) {
            setSelectedYear(selectedMonth.getFullYear());
        }
        setSelectedTab(normalizedTabIndex);
    };

    const changeViewMode = (event, newViewMode) => {
        if (newViewMode === null) return;
        setViewMode(newViewMode);
        try {
            localStorage.setItem(VIEW_MODE_STORAGE_KEY, newViewMode);
        } catch (error) {
            // Speichern ist optional; die Ansicht funktioniert auch ohne localStorage.
        }
    };

    const generateYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = currentYear - 10; year <= currentYear + 10; year++) {
            years.push({ label: year.toString(), value: year });
        }
        return years;
    };

    const employeeOptions = absences
        .map(absence => ({
            id: absence.employeeId,
            label: absence.employeeFirstName + " " + absence.employeeLastName
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "de"));

    const absenceTypes = getAvailableAbsenceTypes(absences);
    const filteredEmployees = getFilteredEmployees(absences, selectedArea, selectedEmployeeIds);

    const periodLabel = isMonthlyView && selectedMonth
        ? MonthNames[selectedMonth.getMonth()] + " " + selectedMonth.getFullYear()
        : selectedYear.toString();

    const filterSummary = () => {
        const areaNames = selectedArea && selectedArea.ordered_areas
            ? selectedArea.ordered_areas.map(area => area.name).join(", ")
            : "Alle";
        const employeeNames = selectedEmployeeIds.length === 0
            ? "Alle"
            : employeeOptions
                .filter(option => selectedEmployeeIds.includes(option.id))
                .map(option => option.label)
                .join(", ");
        const typeNames = selectedTypes.length === 0 ? "Alle" : selectedTypes.join(", ");

        return `Bereich: ${areaNames} | Mitarbeiter: ${employeeNames} | Abwesenheitsarten: ${typeNames}`;
    };

    const generateContent = () => {
        if (isLoading) {
            return <LoadingSpinner />;
        }
        if (hasError) {
            return <p>Ein Fehler ist aufgetreten</p>;
        }
        if (viewMode === "overview") {
            if (isMonthlyView && selectedMonth) {
                return (
                    <AbsenceMonthMatrix
                        employees={filteredEmployees}
                        selectedMonth={selectedMonth}
                        selectedTypes={selectedTypes}
                        periodLabel={periodLabel}
                        filterSummary={filterSummary()}
                    />
                );
            }
            return (
                <AbsenceYearSummary
                    employees={filteredEmployees}
                    selectedYear={selectedYear}
                    selectedTypes={selectedTypes}
                    absenceTypes={absenceTypes}
                />
            );
        }
        return generateDataTable();
    };

    const handlePrint = () => {
        const previousTitle = document.title;
        document.title = "Abwesenheiten - " + periodLabel;
        window.print();
        document.title = previousTitle;
    };

    return (
        <div className="absences-details-container">
            <Link to={'/home/dashboard'}>
                <div className="absences-header-container">
                    <BackButton text="Zurück" />
                </div>
            </Link>
            <div className={"absences-dashboard" + (viewMode === "overview" && isMonthlyView ? " absences-dashboard--matrix-print" : "")}>
                <div className="absences-print-header">
                    <h3>Abwesenheiten – {periodLabel}</h3>
                    <p>{viewMode === "overview" ? "Übersicht" : "Liste"} | {filterSummary()}</p>
                </div>
                <div className="absences-dashboard-header">
                    <h3 className="absences-screen-title">Abwesenheiten</h3>
                    <PrimaryButton text="Drucken" onClick={handlePrint} />
                    <FilterArea selectedArea={selectedArea} setSelectedArea={setSelectedArea} />
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        size="small"
                        onChange={changeViewMode}
                        className="absences-view-toggle"
                    >
                        <ToggleButton value="list">Liste</ToggleButton>
                        <ToggleButton value="overview">Übersicht</ToggleButton>
                    </ToggleButtonGroup>
                </div>
                <div className="absences-filter-row">
                    <FormControl size="small" className="absences-filter-formcontrol">
                        <InputLabel id="absences-employee-filter-label">Mitarbeiter</InputLabel>
                        <Select
                            multiple
                            labelId="absences-employee-filter-label"
                            label="Mitarbeiter"
                            value={selectedEmployeeIds}
                            onChange={(event) => setSelectedEmployeeIds(event.target.value)}
                            input={<OutlinedInput label="Mitarbeiter" />}
                            renderValue={(selected) => selected.length === 0 ? "Alle" : selected.length + " ausgewählt"}
                        >
                            {employeeOptions.map(option => (
                                <MenuItem key={option.id} value={option.id}>
                                    <Checkbox checked={selectedEmployeeIds.includes(option.id)} />
                                    <ListItemText primary={option.label} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" className="absences-filter-formcontrol">
                        <InputLabel id="absences-type-filter-label">Abwesenheitsart</InputLabel>
                        <Select
                            multiple
                            labelId="absences-type-filter-label"
                            label="Abwesenheitsart"
                            value={selectedTypes}
                            onChange={(event) => setSelectedTypes(event.target.value)}
                            input={<OutlinedInput label="Abwesenheitsart" />}
                            renderValue={(selected) => selected.length === 0 ? "Alle" : selected.join(", ")}
                        >
                            {absenceTypes.map(absenceType => (
                                <MenuItem key={absenceType} value={absenceType}>
                                    <Checkbox checked={selectedTypes.includes(absenceType)} />
                                    <ListItemText primary={absenceType} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                <Tabs activeKey={selectedTab} onSelect={changeTab} className="absences-tabs" variant="tabs">
                    <Tab eventKey={0} title="Monatlich">
                        <div className="absences-period-picker">
                            <CustomProvider locale={de_DE}>
                                <DatePicker
                                    className="dashboard-datepicker"
                                    cleanable={false}
                                    label="Ausgewählter Monat"
                                    oneTap
                                    required
                                    format="MMM yyyy"
                                    size="lg"
                                    placeholder="Startmonat"
                                    value={selectedMonth}
                                    onSelect={(value) => setSelectedMonth(value)}
                                />
                            </CustomProvider>
                        </div>
                    </Tab>
                    <Tab eventKey={1} title="Jährlich">
                        <div className="absences-period-picker">
                            <SelectPicker
                                data={generateYearOptions()}
                                style={{ width: 200 }}
                                value={selectedYear}
                                onChange={(value) => setSelectedYear(value)}
                                cleanable={false}
                                placeholder="Ausgewähltes Jahr"
                            />
                        </div>
                    </Tab>
                </Tabs>
                {generateContent()}
            </div>
        </div>
    );
}

export default AbsencesDetails;
